import {
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useFetchFeatureFlags,
  useFetchFields,
  useFetchGames,
  useFetchRanking,
  useFetchTournamentOptions,
} from "../api/hooks";
import { FieldsFieldsType } from "../api/types";
import { PoolGamesType, PoolRankingType } from "../types";

type RootContextType = {
  categories?: string[];
  selectedCategory?: string;
  pools?: string[];
  selectedPool?: string;
  ranking?: PoolRankingType[];
  games?: PoolGamesType[];
  dayPart?: "am" | "pm";
  fields?: FieldsFieldsType[];
  featureFlags?: Record<string, boolean>;
  setSelectedCategory: (category: string) => void;
  setSelectedPool: (pool?: string) => void;
  setDayPart: (dayPart: "am" | "pm") => void;
};

export const RootContext = createContext<RootContextType>({
  categories: [],
  selectedCategory: undefined,
  selectedPool: undefined,
  ranking: undefined,
  games: undefined,
  dayPart: "am",
  fields: undefined,
  featureFlags: undefined,
  setSelectedCategory: () => null,
  setSelectedPool: () => null,
  setDayPart: () => null,
});

export const RootProvider = ({ children }: PropsWithChildren) => {
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [selectedPool, setSelectedPool] = useState<string>();
  const [dayPart, setDayPart] = useState<"am" | "pm">("am");

  const { data: tournamentOptions } = useFetchTournamentOptions(dayPart);

  const { data: ranking, refetch: refetchRanking } = useFetchRanking({
    selectedCategory,
    dayPart,
  });
  const { data: games, refetch: refetchGames } = useFetchGames({
    selectedCategory,
    dayPart,
  });
  const { data: fields } = useFetchFields();
  const { data: featureFlags } = useFetchFeatureFlags();

  useEffect(() => {
    refetchRanking();
    refetchGames();
    setSelectedCategory(undefined);
    setSelectedPool(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayPart]);

  useEffect(() => {
    if (featureFlags?.["is_day_part_pm"]) {
      setDayPart("pm");
    }
  }, [featureFlags]);

  useEffect(() => {
    if (
      tournamentOptions?.categories &&
      tournamentOptions?.categories.length > 0 &&
      !selectedCategory
    ) {
      setSelectedCategory(tournamentOptions?.categories?.[0]);
    }
  }, [tournamentOptions?.categories, selectedCategory]);

  const value = useMemo(
    () => ({
      categories: tournamentOptions?.categories || [],
      selectedCategory,
      pools: tournamentOptions?.pools || [],
      selectedPool,
      ranking,
      games,
      dayPart,
      fields,
      featureFlags,
      setSelectedCategory,
      setSelectedPool,
      setDayPart,
    }),
    [
      tournamentOptions?.categories,
      tournamentOptions?.pools,
      selectedCategory,
      selectedPool,
      ranking,
      games,
      dayPart,
      fields,
      featureFlags,
    ]
  );

  return <RootContext.Provider value={value}>{children}</RootContext.Provider>;
};
